import { type DatasourceResponseSchemaProperty } from '@atlaskit/linking-types';
import Select from '@databases/atlaskit/Select';
import { HelperMessage } from '@databases/form/FormComponents';
import ValidationStatus from '@databases/form/ValidationStatus';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import { type CloudId } from '@databases/types/confluence';
import { loadSupportedFields } from '@databases/utils/redux/datasources/actions';
import React, { type FunctionComponent, useCallback, useEffect } from 'react';
import { useAsync } from 'react-async';
import { useIntl } from 'react-intl-next';

import { type DatasourceType } from '@canvas/atlassian-service/datasource';

import { getCompactSelectStyles } from './compact-select-styles.ts';

type JiraField = DatasourceResponseSchemaProperty;

function validate(
  value: string | undefined,
  isRequired: boolean | undefined,
  validationMessage: string
): string | undefined {
  if (isRequired && (!value || value.trim() === '')) {
    return validationMessage;
  }
}

interface DatasourcesFieldPickerProps {
  cloudId: CloudId;
  datasourceType: DatasourceType;
  value: string | undefined;
  onChange: (value: { value: string; label: string } | undefined) => void;
  isDisabled?: boolean;
  isRequired?: boolean;
  isCompact?: boolean;
  inputId?: string;
  showHelperMessage?: boolean;
}

const compactStyles = getCompactSelectStyles<JiraField>();

const JiraFieldPicker: FunctionComponent<DatasourcesFieldPickerProps> = ({
  cloudId,
  datasourceType,
  value,
  onChange,
  isDisabled,
  isRequired = false,
  isCompact = false,
  inputId,
  showHelperMessage = true
}) => {
  const { formatMessage } = useIntl();
  const [options, setOptions] = React.useState<JiraField[]>([]);
  const [searchValue, setSearchValue] = React.useState<string>('');

  const dispatch = defaultStoreUtils.useDispatch();
  const optionsAsync = useAsync({
    promiseFn: useCallback(async () => {
      if (!cloudId) {
        return;
      }
      return await dispatch(loadSupportedFields(cloudId, datasourceType));
    }, [cloudId, datasourceType, dispatch])
  });

  useEffect(() => {
    const setFilteredOptions = async () => {
      const filtered =
        optionsAsync.data
          ?.filter(
            (field) =>
              !field.key.includes('customfield_') ||
              (searchValue.length > 0 &&
                field.title.toLowerCase().includes(searchValue.toLowerCase()))
          )
          .sort((a, b) => {
            if (a.key.includes('customfield_') && !b.key.includes('customfield_')) {
              return 1;
            } else if (
              !a.key.includes('customfield_') &&
              b.key.includes('customfield_')
            ) {
              return -1;
            }
            return 0;
          }) || [];

      setOptions(filtered || []);
    };

    setFilteredOptions();
  }, [optionsAsync.data, searchValue]);

  const resolvedValue =
    value != null ? optionsAsync.data?.find((field) => field.key === value) : undefined;

  const handleChange = useCallback(
    (selectedOption: JiraField | null) => {
      if (!selectedOption) {
        onChange(undefined);
      } else {
        onChange({ value: selectedOption.key, label: selectedOption.title });
      }
    },
    [onChange]
  );

  const getOptionValue = useCallback((option: JiraField) => option.key, []);
  const getOptionLabel = useCallback(
    (option: JiraField) =>
      `${option.key.includes('customfield_') ? `(${option.key}) ` : ''}${option.title}`,
    []
  );

  return (
    <ValidationStatus
      error={validate(
        value,
        isRequired,
        formatMessage(messages['validation.required-field'])
      )}
    >
      {(validationProps) => (
        <>
          <Select<JiraField>
            inputId={inputId}
            value={resolvedValue}
            options={options}
            onChange={handleChange}
            isDisabled={isDisabled || !cloudId || optionsAsync.isPending}
            isInvalid={validationProps.isInvalid}
            onInputChange={(inputValue) => setSearchValue(inputValue)}
            inputValue={searchValue}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            formatOptionLabel={getOptionLabel}
            isLoading={optionsAsync.isPending}
            placeholder={formatMessage(
              messages[
                'field-settings.type-jira-issue-details-select-jira-field-placeholder'
              ]
            )}
            noOptionsMessage={() => formatMessage(messages['generic-select.no-options'])}
            styles={isCompact ? compactStyles : undefined}
            spacing={isCompact ? 'compact' : undefined}
          />
          {showHelperMessage && (
            <HelperMessage>
              {formatMessage(
                messages['field-settings.type-jira-issue-details-jira-field-description']
              )}
            </HelperMessage>
          )}
        </>
      )}
    </ValidationStatus>
  );
};

export default JiraFieldPicker;
